import React, { useEffect } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
  Container,
} from "reactstrap";
import { useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { Employee } from "features/Employees/types/employee.types";
import {
  createEmployee,
  updateEmployee,
  getEmployee,
} from "features/Employees/services/EmployeeService";
import { employeeRequestToFormData } from "features/Employees/utils/employeeFormPayload";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getUserOptions } from "features/Users/services/UserService";
import { getDepartmentOptions } from "features/Departments/services/DepartmentService";
import {
  type UserOptionAttributes,
  UserOption,
  UserSingleValue,
} from "./UserSelectComponents";
import { getDesignationOptions } from "features/Designations/services/DesignationService";
import { getEmploymentCategoryOptions } from "features/EmploymentCategories/services/EmploymentCategoryService";
import { BackButton } from "components/common/Buttons";
import { getDocumentTitle } from "utils/helpers";
import Flatpickr from "react-flatpickr";
import type { AddEmployeeRequest } from "features/Employees/types/employee.types";

type Mode = "linkExisting" | "createNew";

function joinDateToPickerValue(ymd: string): Date | "" {
  if (!ymd?.trim()) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd.trim());
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function strOrNull(s: string | undefined | null): string | null {
  if (s == null) return null;
  const t = s.trim();
  return t === "" ? null : t;
}

const EmployeeForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(!isEdit ? "New Employee" : "Edit Employee");

  const [record, setRecord] = React.useState<Employee | null>(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      mode: "linkExisting" as Mode,
      // Link existing
      userId: record?.userId ?? ("" as string | number),
      // Create new
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      // Shared
      address: record?.address ?? "",
      city: record?.city ?? "",
      state: record?.state ?? "",
      pincode: record?.pincode ?? "",
      bloodGroup: record?.bloodGroup ?? "",
      departmentId: record?.departmentId ?? ("" as string | number),
      designationId: record?.designationId ?? ("" as string | number),
      employmentCategoryId:
        record?.employmentCategoryId ?? ("" as string | number),
      joinDate: record?.joinDate
        ? new Date(record.joinDate).toISOString().slice(0, 10)
        : "",
      avatar: null as File | null,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const {
          avatar,
          mode,
          firstName,
          lastName,
          email,
          phone,
          userId,
          ...shared
        } = values;

        const sharedFields = {
          departmentId:
            shared.departmentId !== "" ? Number(shared.departmentId) : null,
          designationId:
            shared.designationId !== "" ? Number(shared.designationId) : null,
          employmentCategoryId: Number(shared.employmentCategoryId),
          joinDate: shared.joinDate,
          address: strOrNull(shared.address),
          city: strOrNull(shared.city),
          state: strOrNull(shared.state),
          pincode: strOrNull(shared.pincode),
          bloodGroup: strOrNull(shared.bloodGroup),
        };

        const base: AddEmployeeRequest =
          mode === "createNew"
            ? {
                ...sharedFields,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                phone: strOrNull(phone),
              }
            : {
                ...sharedFields,
                userId: Number(userId),
              };

        const avatarFile = avatar instanceof File ? avatar : null;
        const useMultipart = avatarFile != null;

        const response = isEdit
          ? useMultipart
            ? await updateEmployee(
                Number(id),
                employeeRequestToFormData(
                  { ...base, id: Number(id) },
                  avatarFile,
                ),
              )
            : await updateEmployee(Number(id), { ...base, id: Number(id) })
          : useMultipart
            ? await createEmployee(employeeRequestToFormData(base, avatarFile))
            : await createEmployee(base);

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? "Something went wrong");
      }
    },
  });

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id || isNaN(Number(id))) return;
      try {
        const response = await getEmployee(Number(id));
        if (response.success) setRecord(response.data);
      } catch (err) {
        console.warn(err);
      }
    };
    fetchRecord();
  }, [id]);

  const { values, setFieldValue } = validation;
  const mode = values.mode;

  function switchMode(next: Mode) {
    setFieldValue("mode", next);
    if (next === "createNew") {
      setFieldValue("userId", "");
    } else {
      setFieldValue("firstName", "");
      setFieldValue("lastName", "");
      setFieldValue("email", "");
      setFieldValue("phone", "");
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit Employee" : "New Employee"}
          pageTitle="HR Management"
          items={[{ label: "Employees", path: "/employees" }]}
        />
        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col xl={8}>
              {/* User account */}
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-1">User account</h5>
                  <p className="text-muted mb-0 small">
                    Link an existing user or create a new one for this employee.
                  </p>
                </CardHeader>
                <CardBody>
                  <div
                    className="alert alert-info d-flex gap-2 py-2 px-3 mb-4"
                    role="alert"
                  >
                    <i className="ri-information-line fs-16 flex-shrink-0 mt-1" />
                    <div className="small">
                      <strong>What does an employee profile do?</strong>
                      <p className="mb-0 mt-1">
                        Linking a user to an employee profile allows them to be
                        tracked in <strong>attendance</strong>, included in{" "}
                        <strong>payroll runs</strong>, assigned to{" "}
                        <strong>work orders</strong> and{" "}
                        <strong>site visits</strong>, and apply for{" "}
                        <strong>leave</strong>. Without an employee profile, a
                        user can only log in and use the system based on their
                        role permissions.
                      </p>
                    </div>
                  </div>

                  {!isEdit && (
                    <div
                      className="d-flex rounded border overflow-hidden mb-4"
                      style={{ width: "fit-content" }}
                    >
                      <button
                        type="button"
                        className={`btn btn-sm px-4 rounded-0 ${
                          mode === "linkExisting"
                            ? "btn-primary"
                            : "btn-light text-muted"
                        }`}
                        onClick={() => switchMode("linkExisting")}
                      >
                        <i className="ri-link me-1" />
                        Link existing user
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm px-4 rounded-0 border-start ${
                          mode === "createNew"
                            ? "btn-primary"
                            : "btn-light text-muted"
                        }`}
                        onClick={() => switchMode("createNew")}
                      >
                        <i className="ri-user-add-line me-1" />
                        Create new user
                      </button>
                    </div>
                  )}

                  {/* Link existing */}
                  {(mode === "linkExisting" || isEdit) && (
                    <Row>
                      <Col md={12} className="mb-3">
                        <Label className="form-label">User</Label>
                        <AsyncPaginatedSelect<UserOptionAttributes>
                          key={record?.userId ?? 0}
                          defaultOption={
                            record?.user
                              ? {
                                  value: record.userId,
                                  label:
                                    [
                                      record.user.firstName,
                                      record.user.lastName,
                                    ]
                                      .filter(Boolean)
                                      .join(" ") || record.user.email,
                                  firstName: record.user.firstName,
                                  lastName: record.user.lastName,
                                  email: record.user.email,
                                  avatar: record.user.avatar,
                                  roleNames: record.user.roles
                                    ?.map((r) => r.name)
                                    .filter(Boolean)
                                    .join(", "),
                                }
                              : undefined
                          }
                          fetchOptions={getUserOptions}
                          placeholder="Search and select a user..."
                          value={values.userId}
                          components={{
                            Option: UserOption,
                            SingleValue: UserSingleValue,
                          }}
                          onChange={(val: any) => {
                            setFieldValue("userId", val?.value);
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("userId", true)
                          }
                          isInvalid={
                            validation.touched.userId &&
                            !!validation.errors.userId
                          }
                          isDisabled={isEdit}
                        />
                        {isEdit && (
                          <div className="d-flex align-items-center justify-content-between mt-1">
                            <small className="text-muted">
                              User cannot be changed when editing.
                            </small>
                            {record?.userId && (
                              <Link
                                to={`/user-management/users/edit/${record.userId}`}
                                className="small"
                              >
                                <i className="ri-external-link-line me-1" />
                                Edit user account
                              </Link>
                            )}
                          </div>
                        )}
                        {validation.touched.userId &&
                        validation.errors.userId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.userId}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                  )}

                  {/* Create new */}
                  {mode === "createNew" && !isEdit && (
                    <>
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label className="form-label">
                            First name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="firstName"
                            placeholder="First name"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={values.firstName}
                            invalid={
                              validation.touched.firstName &&
                              !!validation.errors.firstName
                            }
                          />
                          {validation.touched.firstName &&
                          validation.errors.firstName ? (
                            <FormFeedback>
                              {validation.errors.firstName}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label">
                            Last name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="lastName"
                            placeholder="Last name"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={values.lastName}
                            invalid={
                              validation.touched.lastName &&
                              !!validation.errors.lastName
                            }
                          />
                          {validation.touched.lastName &&
                          validation.errors.lastName ? (
                            <FormFeedback>
                              {validation.errors.lastName}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label">
                            Email <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="email"
                            name="email"
                            placeholder="email@example.com"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={values.email}
                            invalid={
                              validation.touched.email &&
                              !!validation.errors.email
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback>
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label className="form-label">Phone</Label>
                          <Input
                            type="text"
                            name="phone"
                            placeholder="Phone number"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={values.phone}
                            invalid={
                              validation.touched.phone &&
                              !!validation.errors.phone
                            }
                          />
                          {validation.touched.phone &&
                          validation.errors.phone ? (
                            <FormFeedback>
                              {validation.errors.phone}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <div
                        className="alert alert-success d-flex align-items-center gap-2 py-2 px-3 mb-0"
                        role="alert"
                      >
                        <i className="ri-mail-send-line fs-16 flex-shrink-0" />
                        <p className="mb-0 small">
                          A welcome email with login credentials will be sent to
                          the employee automatically.
                        </p>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Employment details */}
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-1">Employment details</h5>
                  <p className="text-muted mb-0 small">
                    Department, job title, employment type, and start date
                  </p>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Department</Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.departmentId != null
                              ? {
                                  value: record.departmentId,
                                  label:
                                    record.department?.name ||
                                    `Department #${record.departmentId}`,
                                }
                              : undefined
                          }
                          fetchOptions={getDepartmentOptions}
                          placeholder="Select department..."
                          value={values.departmentId}
                          onChange={(val: any) => {
                            setFieldValue("departmentId", val?.value ?? "");
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("departmentId", true)
                          }
                          isInvalid={
                            validation.touched.departmentId &&
                            !!validation.errors.departmentId
                          }
                        />
                        {validation.touched.departmentId &&
                        validation.errors.departmentId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.departmentId}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Designation</Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.designationId != null
                              ? {
                                  value: record.designationId,
                                  label:
                                    record.designation?.name ||
                                    `Designation #${record.designationId}`,
                                }
                              : undefined
                          }
                          fetchOptions={getDesignationOptions}
                          placeholder="Select designation..."
                          value={values.designationId}
                          onChange={(val: any) => {
                            setFieldValue("designationId", val?.value ?? "");
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("designationId", true)
                          }
                          isInvalid={
                            validation.touched.designationId &&
                            !!validation.errors.designationId
                          }
                        />
                        {validation.touched.designationId &&
                        validation.errors.designationId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.designationId}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Employment category{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.employmentCategoryId != null
                              ? {
                                  value: record.employmentCategoryId,
                                  label:
                                    record.employmentCategory?.name ||
                                    `Employment category #${record.employmentCategoryId}`,
                                }
                              : undefined
                          }
                          fetchOptions={getEmploymentCategoryOptions}
                          placeholder="Select employment category…"
                          value={values.employmentCategoryId}
                          onChange={(val: any) => {
                            setFieldValue(
                              "employmentCategoryId",
                              val?.value ?? "",
                            );
                          }}
                          onBlur={() =>
                            validation.setFieldTouched(
                              "employmentCategoryId",
                              true,
                            )
                          }
                          isInvalid={
                            validation.touched.employmentCategoryId &&
                            !!validation.errors.employmentCategoryId
                          }
                        />
                        {validation.touched.employmentCategoryId &&
                        validation.errors.employmentCategoryId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.employmentCategoryId}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">Join date</Label>
                        <Flatpickr
                          name="joinDate"
                          className={`form-control ${
                            validation.touched.joinDate &&
                            validation.errors.joinDate
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select join date"
                          options={{
                            altInput: true,
                            altFormat: "d M, Y",
                            dateFormat: "Y-m-d",
                            onClose: () =>
                              validation.setFieldTouched("joinDate", true),
                          }}
                          onChange={(selectedDates: Date[]) => {
                            const d = selectedDates?.[0];
                            if (!d) {
                              setFieldValue("joinDate", "");
                              return;
                            }
                            const y = d.getFullYear();
                            const mo = String(d.getMonth() + 1).padStart(
                              2,
                              "0",
                            );
                            const day = String(d.getDate()).padStart(2, "0");
                            setFieldValue("joinDate", `${y}-${mo}-${day}`);
                          }}
                          value={joinDateToPickerValue(values.joinDate || "")}
                          disabled={isEdit}
                        />
                        {isEdit ? (
                          <small className="text-muted">
                            Join date cannot be changed after the employee is created.
                          </small>
                        ) : validation.touched.joinDate && validation.errors.joinDate ? (
                          <FormFeedback type="invalid">
                            {validation.errors.joinDate}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-1">
                    Address & personal details
                  </h5>
                  <p className="text-muted mb-0 small">
                    Location and personal information
                  </p>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label">Address</Label>
                    <Input
                      type="textarea"
                      className="form-control"
                      placeholder="Address"
                      name="address"
                      rows={2}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={values.address || ""}
                      invalid={
                        validation.touched.address &&
                        !!validation.errors.address
                      }
                    />
                    {validation.touched.address && validation.errors.address ? (
                      <FormFeedback type="invalid">
                        {validation.errors.address}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">City</Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="City"
                      name="city"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={values.city || ""}
                      invalid={
                        validation.touched.city && !!validation.errors.city
                      }
                    />
                    {validation.touched.city && validation.errors.city ? (
                      <FormFeedback type="invalid">
                        {validation.errors.city}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">State</Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="State"
                      name="state"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={values.state || ""}
                      invalid={
                        validation.touched.state && !!validation.errors.state
                      }
                    />
                    {validation.touched.state && validation.errors.state ? (
                      <FormFeedback type="invalid">
                        {validation.errors.state}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">Pincode</Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="Pincode"
                      name="pincode"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={values.pincode || ""}
                      invalid={
                        validation.touched.pincode &&
                        !!validation.errors.pincode
                      }
                    />
                    {validation.touched.pincode && validation.errors.pincode ? (
                      <FormFeedback type="invalid">
                        {validation.errors.pincode}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">Blood group</Label>
                    <Input
                      type="text"
                      className="form-control"
                      placeholder="e.g. A+, B-, O+"
                      name="bloodGroup"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={values.bloodGroup || ""}
                      invalid={
                        validation.touched.bloodGroup &&
                        !!validation.errors.bloodGroup
                      }
                    />
                    {validation.touched.bloodGroup &&
                    validation.errors.bloodGroup ? (
                      <FormFeedback type="invalid">
                        {validation.errors.bloodGroup}
                      </FormFeedback>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col>
              <div className="d-flex justify-content-end gap-2 flex-wrap mb-3">
                <BackButton />
                <button
                  type="submit"
                  className="btn btn-primary w-sm"
                  disabled={validation.isSubmitting}
                >
                  {validation.isSubmitting
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                      ? "Update"
                      : "Create"}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default EmployeeForm;
